import type { ActivityFilterCategory } from '@/api/activities';
import { useTranslation } from 'react-i18next';

// --- Category filter ---

interface CategoryOption {
  value: ActivityFilterCategory;
  labelKey: string;
}

// Translated at render time (module-scope constants run before the bundles load)
const CATEGORIES: CategoryOption[] = [
  { value: 'all', labelKey: 'web:activityFilters.all' },
  { value: 'expenses', labelKey: 'web:activityFilters.expenses' },
  { value: 'settlements', labelKey: 'web:activityFilters.settlements' },
  { value: 'comments', labelKey: 'web:activityFilters.comments' },
  { value: 'members', labelKey: 'web:activityFilters.members' },
];

// --- Group filter ---

interface GroupOption {
  id: string;
  name: string;
  color: string;
}

interface ActivityFiltersProps {
  category: ActivityFilterCategory;
  onCategoryChange: (category: ActivityFilterCategory) => void;
  groups?: GroupOption[];
  selectedGroupId?: string;
  onGroupChange?: (groupId: string | undefined) => void;
}

export function ActivityFilters({
  category,
  onCategoryChange,
  groups,
  selectedGroupId,
  onGroupChange,
}: ActivityFiltersProps) {
  const { t } = useTranslation();
  const showGroupFilter = groups && groups.length > 1 && onGroupChange;

  return (
    <div className="space-y-3">
      {/* Category filter tabs */}
      <div className="flex items-center gap-1 rounded-lg bg-surface-tertiary p-1">
        {CATEGORIES.map(opt => (
          <button
            key={opt.value}
            onClick={() => onCategoryChange(opt.value)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              category === opt.value
                ? 'bg-surface-primary text-text-primary shadow-sm'
                : 'text-text-tertiary hover:text-text-secondary'
            }`}
          >
            {t(opt.labelKey)}
          </button>
        ))}
      </div>

      {/* Group filter chips */}
      {showGroupFilter && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => onGroupChange(undefined)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              !selectedGroupId
                ? 'bg-gray-900 text-white dark:bg-brand-600 dark:text-white'
                : 'bg-surface-tertiary text-text-secondary hover:bg-surface-inset'
            }`}
          >
            {t('expenses:filter.allGroups')}
          </button>
          {groups.map(g => (
            <button
              key={g.id}
              onClick={() => onGroupChange(g.id)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                selectedGroupId === g.id
                  ? 'bg-gray-900 text-white dark:bg-brand-600 dark:text-white'
                  : 'bg-surface-tertiary text-text-secondary hover:bg-surface-inset'
              }`}
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: g.color }}
              />
              {g.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
